import PageHero from "@/components/PageHero";
import { FadeUp, StaggerChildren, StaggerItem } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";

const strengths = [
  { k: "Market Experience", d: "Decades of combined transactional and market cycle exposure across Singapore residential, commercial and industrial property." },
  { k: "Research and Analysis", d: "A shared research discipline — primary-source data, modelled feasibility, and structured risk assessment." },
  { k: "Client Advisory", d: "Personalised strategy development, clear communication, and long-term client relationships beyond a single transaction." },
];


export default function AdvisoryTeam() {
  return (
    <>
      <SeoHead pageKey="advisory_team" />
      <PageHero
        eyebrow="Our Advisory Team"
        chapter="Chapter — Team"
        lines={["Collective Experience.", "Shared Commitment."]}
        blurb="Team of perspectives. One integrated advisory approach."
        image="/assets/hero-advisory-team"
        imageAlt="PIRAC advisors gathered around a boardroom table reviewing property research, with client objectives on a whiteboard and Singapore property market trends on screen"
      />

      <section className="bg-[#060f1e] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-6">
            <FadeUp>
              {/*
                Three photographs rather than one. A single portrait framed the
                practice around one person, which is the opposite of the point
                this page makes.
              */}
              <div className="grid grid-cols-1 gap-px bg-[#1a2940]">
                <div className="relative overflow-hidden aspect-[16/10] bg-[#0c1728]">
                  <img
                    src="/assets/team-advisory-2-1300.webp"
                    srcSet="/assets/team-advisory-2-720.webp 720w, /assets/team-advisory-2-1300.webp 1300w"
                    sizes="(min-width: 1024px) 46vw, 90vw"
                    width="1300"
                    height="867"
                    alt="PIRAC advisors in discussion during a client engagement"
                    className="w-full h-full object-cover opacity-90"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#060f1e]/50 via-transparent to-transparent" />
                </div>
                <div className="relative overflow-hidden aspect-[16/10] bg-[#0c1728]">
                  <img
                    src="/assets/team-advisory-3-1300.webp"
                    srcSet="/assets/team-advisory-3-720.webp 720w, /assets/team-advisory-3-1300.webp 1300w"
                    sizes="(min-width: 1024px) 46vw, 90vw"
                    width="1300"
                    height="867"
                    alt="PIRAC advisory team analysing Singapore property market data"
                    className="w-full h-full object-cover opacity-90"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="mt-6">
                <div className="overline text-[#cca24d]/80 text-[10px] mb-2">Advisory in session</div>
                <div className="font-serif text-2xl md:text-3xl text-[#f7f5f0] leading-tight">
                  Team of perspectives, one recommendation.
                </div>
              </div>
            </FadeUp>
          </div>

          <div className="col-span-12 lg:col-span-5 lg:col-start-8">
            <FadeUp><span className="overline">Our Team</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl text-[#f7f5f0] font-light tracking-tight leading-[1.05]">
                An integrated advisory group.
              </h2>
            </FadeUp>
            <FadeUp delay={0.15} className="mt-8 space-y-5 text-[#d8d8d8] leading-relaxed">
              <p>
                PIRAC brings together three experienced property advisors who combine their knowledge,
                market experience and professional judgement to provide clients with a broader and more
                considered perspective on real estate.
              </p>
              <p>
                We are deliberately structured as an integrated advisory group, rather than a collection
                of individual agents working independently. Every client engagement draws upon the
                collective experience of the team, allowing different perspectives to be considered
                before a view is formed.
              </p>
              <p>
                Each advisor brings complementary expertise across residential, commercial, industrial
                and investment property, together with a deep understanding of market dynamics, property
                fundamentals and the practical realities of ownership and investment.
              </p>
              <p>
                Our advisors work through a shared research and advisory methodology, challenging
                assumptions, examining opportunities from different angles and identifying the risks and
                considerations that may otherwise be overlooked. This collaborative approach creates
                greater depth in our analysis and greater confidence in the advice we provide.
              </p>
              <p>
                Most importantly, the team is aligned around one objective — the client's best interests.
                There are no competing individual agendas. The knowledge and experience of three advisors
                are brought together to support one client relationship, one strategic direction and one
                informed decision.
              </p>
              <p>
                We believe the strength of PIRAC is not simply in the experience of each advisor, but in
                the collective intelligence created when that experience is brought together.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="mt-8 pt-6 border-t border-[#1a2940] overline text-[#cca24d]/80">
                Team of advisors. Collective expertise. One shared commitment to better property decisions.
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="bg-[#0c1728] border-y border-[#1a2940] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <FadeUp><span className="overline">Team Strengths</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 mb-14 font-serif text-4xl md:text-5xl text-[#f7f5f0] font-light tracking-tight max-w-3xl">
              Three complementary strengths.
            </h2>
          </FadeUp>
          <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1a2940]">
            {strengths.map((s, i) => (
              <StaggerItem key={s.k} className="bg-[#0c1728] p-10">
                <div className="text-xs text-[#cca24d] tnum mb-6">0{i + 1}</div>
                <h3 className="font-serif text-2xl text-[#f7f5f0] mb-4">{s.k}</h3>
                <p className="text-sm text-[#9ca3af] leading-relaxed">{s.d}</p>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* ----------------------------------------- Eight steps, one methodology */}
      <section className="bg-[#060f1e] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <FadeUp><span className="overline">How The Team Works</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight max-w-3xl leading-[1.05]">
              Eight Steps. <em className="text-[#cca24d] not-italic font-normal italic">One Methodology.</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mt-8 mb-14 max-w-3xl text-lg text-[#d8d8d8] font-light leading-relaxed">
              Our advisory process is designed to bring structure, rigour and clarity to every property
              decision. While every client and engagement is different, our methodology provides a
              consistent framework for understanding the client, researching the opportunity, evaluating
              alternatives and arriving at an informed recommendation.
            </p>
          </FadeUp>

          <FadeUp>
            <a
              href="/assets/team-investor-journey.jpeg"
              target="_blank"
              rel="noopener noreferrer"
              className="block border border-[#1a2940] hover:border-[#cca24d]/50 transition-colors"
              aria-label="The eight-step real estate investor journey — open full size"
            >
              <img
                src="/assets/team-investor-journey-1536.webp"
                srcSet="/assets/team-investor-journey-800.webp 800w, /assets/team-investor-journey-1536.webp 1536w"
                sizes="(min-width: 1024px) 94vw, 92vw"
                alt="The eight-step real estate investor journey: define your investment goals, conduct market research, start preapproval for your loan, find the right property, make an offer and negotiate, secure the financing you need, close on your investment property, and own and operate it"
                className="w-full h-auto block"
                width="1536"
                height="1024"
                loading="lazy"
              />
            </a>
            <a
              href="/assets/team-investor-journey.jpeg"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 min-h-11 py-2 inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors"
            >
              <span className="h-px w-6 bg-[#cca24d]" />
              Open the full-size diagram
            </a>
          </FadeUp>

        </div>
      </section>

      {/* Sector Coverage — collective reach of the advisory team */}
      <section className="bg-[#060f1e] py-24 lg:py-32 border-t border-[#1a2940]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 mb-14 items-end">
            <div className="col-span-12 lg:col-span-8">
              <FadeUp><span className="overline">Team Coverage</span></FadeUp>
              <FadeUp delay={0.1}>
                <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight leading-[1.05]">
                  What the team covers, together.
                </h2>
              </FadeUp>
            </div>
            <div className="col-span-12 lg:col-span-4 lg:text-right">
              <FadeUp delay={0.15}>
                <p className="text-sm text-[#9ca3af] max-w-sm lg:ml-auto">
                  Each advisor brings a primary focus. Every engagement is reviewed by the team. Clients access the full range through a single relationship.
                </p>
              </FadeUp>
            </div>
          </div>
          <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1a2940]">
            {[
              { k: "Residential", n: "01", d: "HDB, private and landed. Owner-occupation, upgrade planning and restructuring across life stages." },
              { k: "Commercial", n: "02", d: "Boutique offices, retail and mixed-use — tenant-level diligence, feasibility and lease planning." },
              { k: "Industrial", n: "03", d: "B1/B2 industrial and logistics facilities. Tenure, zoning and occupancy-cost modelling." },
              { k: "Investment Portfolios", n: "04", d: "Multi-asset property portfolios. Rebalancing, consolidation and decoupling strategy." },
              { k: "Financing & CPF", n: "05", d: "LTV, MSR, TDSR and CPF sequencing. Coordinated with the client's lender and lawyer." },
              { k: "Cycle & Policy", n: "06", d: "Cycle-aware entry / exit reads, and interpretation of IRAS, HDB, MAS and URA policy movement." },
            ].map((s) => (
              <StaggerItem key={s.k} className="bg-[#0c1728] p-8 lg:p-10 group hover:bg-[#0f1c33] transition-colors">
                <div className="text-xs text-[#cca24d]/70 tnum mb-6">{s.n}</div>
                <h3 className="font-serif text-2xl text-[#f7f5f0] mb-4 group-hover:text-[#cca24d] transition-colors">{s.k}</h3>
                <p className="text-sm text-[#9ca3af] leading-relaxed">{s.d}</p>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* Standards the team holds to */}
      <section className="bg-[#0c1728] border-y border-[#1a2940] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <FadeUp><span className="overline">Standards</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl text-[#f7f5f0] font-light tracking-tight leading-[1.05]">
                Four commitments the team holds to.
              </h2>
            </FadeUp>
          </div>
          <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1a2940]">
            {[
              { k: "One recommendation", d: "Clients receive a single, agreed position from the team — not parallel opinions to reconcile." },
              { k: "Sources named", d: "Every regulatory reference (IRAS, HDB, CPF Board, MAS, URA) is stated in the recommendation. No black-box advice." },
              { k: "Alternatives modelled", d: "'Do nothing' is always one of the options presented. Recommendations are chosen against the alternative — not into a vacuum." },
              { k: "Post-engagement review", d: "The strategy is revisited on request, and always when a material policy change lands." },
            ].map((s, i) => (
              <StaggerItem key={s.k} className="bg-[#0c1728] p-10">
                <div className="flex items-baseline gap-5 mb-5">
                  <span className="font-serif text-3xl text-[#cca24d] tnum">0{i + 1}</span>
                  <h3 className="font-serif text-2xl text-[#f7f5f0]">{s.k}</h3>
                </div>
                <p className="text-sm text-[#9ca3af] leading-relaxed pl-12">{s.d}</p>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>
    </>
  );
}
